import { format } from 'date-fns';
import { EnhancedClaim } from '@/lib/claimsTrackerData';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { 
  CheckCircle2, 
  Clock, 
  Send, 
  XCircle, 
  Activity, 
  Shield, 
  Zap,
  FileText,
  AlertTriangle,
  ChevronRight
} from 'lucide-react';

const STATUS_CONFIG: Record<string, { label: string; icon: typeof Clock; color: string }> = {
  paid: { label: 'Paid', icon: CheckCircle2, color: 'bg-success/20 text-success border-success/30' },
  submitted: { label: 'Submitted', icon: Send, color: 'bg-primary/20 text-primary border-primary/30' },
  pending: { label: 'Pending', icon: Clock, color: 'bg-warning/20 text-warning border-warning/30' },
  denied: { label: 'Denied', icon: XCircle, color: 'bg-destructive/20 text-destructive border-destructive/30' },
  coding: { label: 'Coding', icon: Activity, color: 'bg-chart-2/20 text-chart-2 border-chart-2/30' },
  denial_check: { label: 'Checking', icon: Shield, color: 'bg-chart-3/20 text-chart-3 border-chart-3/30' },
  auto_fix: { label: 'Auto-Fix', icon: Zap, color: 'bg-chart-4/20 text-chart-4 border-chart-4/30' },
  note_signed: { label: 'Signed', icon: FileText, color: 'bg-muted text-muted-foreground border-border' },
};

interface ClaimCardProps {
  claim: EnhancedClaim;
  onClick: () => void;
}

export function ClaimCard({ claim, onClick }: ClaimCardProps) {
  const statusConfig = STATUS_CONFIG[claim.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const hasDrift = claim.driftAlerts.length > 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left glass-card p-4 transition-all duration-200",
        "active:scale-[0.98] hover:border-primary/50",
        "min-h-[88px]"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left: Status Icon & Patient Info */}
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={cn(
            "p-2 rounded-lg shrink-0 relative",
            statusConfig.color.split(' ')[0]
          )}>
            <StatusIcon className="w-4 h-4" />
            {hasDrift && (
              <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-warning rounded-full animate-pulse" />
            )}
          </div>
          
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs text-muted-foreground">
                {claim.patientInitials}-{claim.patientDOBLast4}
              </span>
              <Badge variant="outline" className={cn('text-[10px] border', statusConfig.color)}>
                {statusConfig.label}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cn(
                "text-sm font-medium truncate",
                claim.provider === 'Dr. Larkin' && "text-primary"
              )}>
                {claim.provider}
              </span>
              <span className="text-xs text-muted-foreground">
                {format(claim.encounterDate, 'MMM d')}
              </span>
            </div>

            <div className="flex items-center gap-2 mt-1">
              {claim.cptCodes.slice(0, 2).map((code) => (
                <Badge key={code} variant="secondary" className="font-mono text-[10px] bg-secondary/80">
                  {code}
                </Badge>
              ))}
              {claim.cptCodes.length > 2 && (
                <span className="text-[10px] text-muted-foreground">
                  +{claim.cptCodes.length - 2}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: Amount & Arrow */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right">
            <div className="font-mono text-sm font-semibold">
              ${claim.expectedAmount.toLocaleString()}
            </div>
            <div className="flex items-center gap-1 justify-end">
              <span className={cn(
                "font-mono text-[10px] font-medium",
                claim.confidenceScore >= 95 ? "text-success" : 
                claim.confidenceScore >= 90 ? "text-warning" : "text-destructive"
              )}>
                {claim.confidenceScore.toFixed(0)}%
              </span>
              {hasDrift && <AlertTriangle className="w-3 h-3 text-warning" />}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    </button>
  );
}
